/**
 * RanksPanel — global leaderboard + local run history.
 * Data comes from the Cloudflare Worker (`/api/scores/top`); when the API
 * isn't reachable (local preview, offline), we show local bests instead.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Crown, RefreshCw, Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PanelSection } from '@/components/game/ui';
import { money, num } from '@/game/format';
import { fetchTopScores, getLocalRunSeeds, getLocalRuns, type ScoreRow } from '@/game/leaderboard';
import type { Difficulty } from '@/game/types';

type Filter = 'all' | Difficulty;
const FILTERS: Filter[] = ['all', 'easy', 'normal', 'hard', 'realistic'];

export function RanksPanel() {
  const [filter, setFilter] = useState<Filter>('all');

  const topQuery = useQuery({
    queryKey: ['leaderboard'],
    queryFn: fetchTopScores,
  });

  const mySeeds = useMemo(() => getLocalRunSeeds(), []);
  const localRuns = useMemo(
    () => [...getLocalRuns()].sort((a, b) => b.rev - a.rev).slice(0, 10),
    [],
  );

  const rows: ScoreRow[] = useMemo(() => {
    const all = topQuery.data?.rows ?? [];
    return (filter === 'all' ? all : all.filter((r) => r.d === filter)).slice(0, 25);
  }, [topQuery.data, filter]);

  const unreachable = Boolean(topQuery.data && !topQuery.data.ok) || Boolean(topQuery.error);

  return (
    <div className="space-y-6">
      <PanelSection
        title="Global Ranks"
        desc="Empires are ranked by lifetime revenue. Finished runs — exits and bankruptcies alike — earn their place in history."
        right={
          <Button size="sm" variant="outline" onClick={() => void topQuery.refetch()} disabled={topQuery.isFetching}>
            <RefreshCw className={cn('mr-1.5 h-4 w-4', topQuery.isFetching && 'animate-spin motion-reduce:hidden')} /> Refresh
          </Button>
        }
      >
        <div className="flex flex-wrap gap-1.5 pb-1">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              aria-pressed={filter === f}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-medium capitalize text-muted-foreground transition-colors hover:border-primary/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                filter === f && 'border-primary bg-primary/10 text-primary',
              )}
            >
              {f}
            </button>
          ))}
        </div>

        {unreachable ? (
          <Card className="border-dashed">
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              Global rankings unreachable right now — the rankings API lives on the deployed site.
            </CardContent>
          </Card>
        ) : rows.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              No empires on the board yet. Finish a run — even bankruptcy makes history.
            </CardContent>
          </Card>
        ) : (
          <div className="overflow-x-auto rounded-xl border bg-card">
            <table className="w-full min-w-[520px] text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2 font-medium">#</th>
                  <th className="px-4 py-2 font-medium">Company</th>
                  <th className="px-4 py-2 font-medium">Mode</th>
                  <th className="px-4 py-2 text-right font-medium">Revenue</th>
                  <th className="px-4 py-2 text-right font-medium">Units</th>
                  <th className="px-4 py-2 text-right font-medium">Day</th>
                  <th className="px-4 py-2 text-right font-medium">End</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const isMine = r.s !== undefined && mySeeds.has(r.s);
                  return (
                    <tr key={`${r.t}-${i}`} className={cn('border-b last:border-0', isMine && 'bg-primary/5')}>
                      <td className="px-4 py-2 font-semibold tabular-nums">
                        {i === 0 ? <Crown className="h-4 w-4 text-amber-500" aria-label="Rank 1" /> : i + 1}
                      </td>
                      <td className="px-4 py-2">
                        <span className="font-medium">{r.c}</span>
                        {isMine && <Badge variant="secondary" className="ml-2">you</Badge>}
                      </td>
                      <td className="px-4 py-2 capitalize text-muted-foreground">{r.d}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{money(r.rev)}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{num(r.units)}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{num(r.day)}</td>
                      <td className="px-4 py-2 text-right">
                        {r.reason === 'acquired' ? (
                          <span className="text-emerald-600 dark:text-emerald-400" title={`Exit${r.val ? ` for ${money(r.val)}` : ''}`}>Exit</span>
                        ) : (
                          <span className="text-red-600 dark:text-red-400">Bust</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </PanelSection>

      {/* Local history */}
      <PanelSection title="Your Run History" desc="Stored on this device — survives offline.">
        {localRuns.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Trophy className="h-4 w-4" /> Finish your first run to start the collection.
            </CardContent>
          </Card>
        ) : (
          <ul className="space-y-2">
            {localRuns.map((r, i) => (
              <li key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-card px-4 py-3 text-sm">
                <div>
                  <span className="font-medium">{money(r.rev)}</span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    {r.reason === 'acquired' ? `exit${r.val ? ` @ ${money(r.val)}` : ''}` : 'bankrupt'} · day {num(r.day)} · {r.d} · {new Date(r.submittedAt).toLocaleDateString()}
                  </span>
                </div>
                {r.rank != null ? (
                  <Badge variant="outline" className="border-emerald-600/40 text-emerald-600 dark:text-emerald-400">Global #{r.rank}</Badge>
                ) : (
                  <Badge variant="outline">unranked</Badge>
                )}
              </li>
            ))}
          </ul>
        )}
      </PanelSection>
    </div>
  );
}
